import {
  ApiError,
  ApiErrorInitialState,
  GrantTag,
  NewTag,
  ProjectTag,
} from '@hellodarwin/core/lib/features/entities';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApiV2 from '../client-api-v2';

const grantTagsAdapter = createEntityAdapter({
  selectId: (tag: GrantTag) => tag.id,
});

const projectTagsAdapter = createEntityAdapter({
  selectId: (tag: ProjectTag) => tag.id,
});

interface GrantTagsState {
  status: 'idle' | 'pending';
  error: ApiError;
  grantTags: EntityState<GrantTag, string>;
  projectTags: EntityState<ProjectTag, string>;
  selectedTags: NewTag[];
  goalOptions: { entities: Record<string, NewTag>; loading: boolean };
  expenseOptions: { entities: Record<string, NewTag>; loading: boolean };
}

const initialState: GrantTagsState = {
  status: 'idle',
  error: ApiErrorInitialState,
  grantTags: grantTagsAdapter.getInitialState(),
  projectTags: projectTagsAdapter.getInitialState(),
  selectedTags: [],
  goalOptions: { entities: {}, loading: false },
  expenseOptions: { entities: {}, loading: false },
};

export const fetchGoalTags = createAsyncThunk<
  NewTag[],
  { api: ClientApiV2; locale: string },
  { rejectValue: ApiError }
>('admin/fetchGoalTags', async ({ api, locale }, { rejectWithValue }) => {
  try {
    const response = await api.get<NewTag[]>(
      `/grants/tags/goal?locale=${locale}`,
    );
    return response.data;
  } catch (err: any) {
    console.error(err.response.data);
    return rejectWithValue(err.response.data);
  }
});

export const fetchExpenseTags = createAsyncThunk<
  NewTag[],
  { api: ClientApiV2; locale: string },
  { rejectValue: ApiError }
>('client/fetchExpenseTags', async ({ api, locale }, { rejectWithValue }) => {
  try {
    const response = await api.get<NewTag[]>(
      `/grants/tags/expense?locale=${locale}`,
    );
    return response.data;
  } catch (err: any) {
    console.error(err.response.data);
    return rejectWithValue(err.response.data);
  }
});

// Grant Tags
export const generateGrantTags = createAsyncThunk<
  GrantTag[],
  { api: ClientApiV2; grantId: string },
  { rejectValue: ApiError }
>('admin/generateGrantTags', async ({ api, grantId }, { rejectWithValue }) => {
  try {
    const response = await api.post<GrantTag[]>(
      `/grants/tags/generate/${grantId}`,
    );
    return response.data;
  } catch (err: any) {
    console.error(err.response.data);
    showErrorNotification(err.response.data);
    return rejectWithValue(err.response.data);
  }
});

export const deleteAllGrantTags = createAsyncThunk<
  string,
  { api: ClientApiV2; grantId: string },
  { rejectValue: ApiError }
>('admin/deleteAllGrantTags', async ({ api, grantId }, { rejectWithValue }) => {
  try {
    const response = await api.delete<string>(`/grants/${grantId}/tags`);
    return response.data;
  } catch (err: any) {
    console.error(err.response.data);
    showErrorNotification(err.response.data);
    return rejectWithValue(err.response.data);
  }
});

export const fetchGrantTags = createAsyncThunk<
  GrantTag[],
  { api: ClientApiV2; grantId: string; locale: string },
  { rejectValue: ApiError }
>(
  'admin/fetchGrantTags',
  async ({ api, grantId, locale }, { rejectWithValue }) => {
    try {
      const response = await api.get<GrantTag[]>(
        `/grants/${grantId}/tags?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const addGrantTags = createAsyncThunk<
  GrantTag[],
  { api: ClientApiV2; grantId: string; tags: string[] },
  { rejectValue: ApiError }
>('admin/addGrantTags', async ({ api, grantId, tags }, { rejectWithValue }) => {
  try {
    const response = await api.post<GrantTag[]>(`/grants/${grantId}/tags`, {
      tagIds: tags,
    });
    return response.data;
  } catch (err: any) {
    console.error(err.response.data);
    return rejectWithValue(err.response.data);
  }
});

// Project Tags
export const generateProjectTags = createAsyncThunk<
  ProjectTag[],
  { api: ClientApiV2; projectId: string },
  { rejectValue: ApiError }
>(
  'admin/generateProjectTags',
  async ({ api, projectId }, { rejectWithValue }) => {
    try {
      const response = await api.post<ProjectTag[]>(
        `/projects/tags/generate/${projectId}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteAllProjectTags = createAsyncThunk<
  string,
  { api: ClientApiV2; projectId: string },
  { rejectValue: ApiError }
>(
  'admin/deleteAllProjectTags',
  async ({ api, projectId }, { rejectWithValue }) => {
    try {
      const response = await api.delete<string>(`/projects/${projectId}/tags`);
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchProjectTags = createAsyncThunk<
  ProjectTag[],
  { api: ClientApiV2; projectId: string; locale: string },
  { rejectValue: ApiError }
>(
  'admin/fetchProjectTags',
  async ({ api, projectId, locale }, { rejectWithValue }) => {
    try {
      const response = await api.get<ProjectTag[]>(
        `/projects/${projectId}/tags?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const addProjectTags = createAsyncThunk<
  ProjectTag[],
  { api: ClientApiV2; projectId: string; tags: string[] },
  { rejectValue: ApiError }
>(
  'admin/addProjectTags',
  async ({ api, projectId, tags }, { rejectWithValue }) => {
    try {
      const response = await api.post<ProjectTag[]>(
        `/projects/${projectId}/tags`,
        {
          tagIds: tags,
        },
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateProjectTags = createAsyncThunk<
  ProjectTag[],
  { api: ClientApiV2; projectId: string; tags: string[] },
  { rejectValue: ApiError }
>(
  'admin/updateProjectTags',
  async ({ api, projectId, tags }, { rejectWithValue }) => {
    try {
      const response = await api.put<ProjectTag[]>(
        `/projects/${projectId}/tags`,
        {
          tagIds: tags,
        },
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

const tagsV2Slice = createSlice({
  name: 'tagsV2',
  initialState,
  reducers: {
    setSelectedTags(state, { payload }) {
      state.selectedTags = payload;
    },
    clearSelectedTags(state) {
      state.selectedTags = [];
    },
  },
  extraReducers: (builder) => {
    // Project Tags
    builder
      .addCase(generateProjectTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(generateProjectTags.fulfilled, (state, { payload }) => {
        projectTagsAdapter.setAll(state.projectTags, payload || []);
        state.status = 'idle';
      })
      .addCase(generateProjectTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status = 'idle';
      });
    builder
      .addCase(deleteAllProjectTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(deleteAllProjectTags.fulfilled, (state) => {
        projectTagsAdapter.setAll(state.projectTags, []);
        state.status = 'idle';
      })
      .addCase(deleteAllProjectTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status = 'idle';
      });
    builder
      .addCase(fetchProjectTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchProjectTags.fulfilled, (state, action) => {
        state.status = 'idle';
        projectTagsAdapter.setAll(state.projectTags, action.payload || []);
      })
      .addCase(fetchProjectTags.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload!;
      })
      .addCase(addProjectTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
      })
      .addCase(addProjectTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(addProjectTags.fulfilled, (state, { payload }) => {
        projectTagsAdapter.setAll(state.projectTags, payload || []);
        state.status = 'idle';
      })
      .addCase(updateProjectTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
      })
      .addCase(updateProjectTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(updateProjectTags.fulfilled, (state, { payload }) => {
        projectTagsAdapter.setAll(state.projectTags, payload || []);
        state.status = 'idle';
      });
    // Grant Tags
    builder
      .addCase(generateGrantTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(generateGrantTags.fulfilled, (state, { payload }) => {
        grantTagsAdapter.setAll(state.grantTags, payload || []);
        state.status = 'idle';
      })
      .addCase(generateGrantTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status = 'idle';
      });
    builder
      .addCase(deleteAllGrantTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(deleteAllGrantTags.fulfilled, (state) => {
        grantTagsAdapter.setAll(state.grantTags, []);
        state.status = 'idle';
      })
      .addCase(deleteAllGrantTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status = 'idle';
      });
    builder
      .addCase(fetchGrantTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchGrantTags.fulfilled, (state, action) => {
        state.status = 'idle';
        grantTagsAdapter.setAll(state.grantTags, action.payload || []);
      })
      .addCase(fetchGrantTags.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload!;
      })

      .addCase(fetchGoalTags.pending, (state) => {
        state.goalOptions.loading = true;
      })
      .addCase(fetchGoalTags.fulfilled, (state, { payload }) => {
        const entities = payload?.reduce<Record<string, NewTag>>((acc, tag) => {
          acc[tag.tag_id] = tag;
          return acc;
        }, {});
        state.goalOptions.entities = entities ?? {};
        state.goalOptions.loading = false;
      })
      .addCase(fetchGoalTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.goalOptions.loading = false;
      })
      .addCase(fetchExpenseTags.pending, (state) => {
        state.expenseOptions.loading = true;
      })
      .addCase(fetchExpenseTags.fulfilled, (state, { payload }) => {
        const entities = payload?.reduce<Record<string, NewTag>>((acc, tag) => {
          acc[tag.tag_id] = tag;
          return acc;
        }, {});
        state.expenseOptions.entities = entities ?? {};
        state.expenseOptions.loading = false;
      })
      .addCase(fetchExpenseTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.expenseOptions.loading = false;
      })
      .addCase(addGrantTags.rejected, (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
      })
      .addCase(addGrantTags.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(addGrantTags.fulfilled, (state, { payload }) => {
        grantTagsAdapter.setAll(state.grantTags, payload || []);
        state.status = 'idle';
      });
  },
});

export const { setSelectedTags, clearSelectedTags } = tagsV2Slice.actions;

export const selectGoalTagsOptions = (state: RootState) =>
  state.tagsV2.goalOptions.entities;
export const selectExpenseTagsOptions = (state: RootState) =>
  state.tagsV2.expenseOptions.entities;
export const selectSelectedTags = (state: RootState) =>
  state.tagsV2.selectedTags;

export const { selectAll: selectGrantTags, selectById: selectGrantTagById } =
  grantTagsAdapter.getSelectors((state: RootState) => state.tagsV2.grantTags);

export const {
  selectAll: selectProjectTags,
  selectById: selectProjectTagById,
} = projectTagsAdapter.getSelectors(
  (state: RootState) => state.tagsV2.projectTags,
);

export const selectSearchableGoalTags = createSelector(
  [(state: RootState) => state.tagsV2.goalOptions],
  (tags) => {
    const tagsArray = Object.values(tags.entities);
    return Array.isArray(tagsArray) && tagsArray.length > 0
      ? tagsArray.map((item) => {
          return {
            label: item.content,
            short_label: item.short_content,
            value: item.tag_id,
            groupLabel: item.parent_content,
            target_legal_structure: item.target_legal_structure,
          };
        })
      : [];
  },
);

export const selectSearchableExpenseTags = createSelector(
  [selectExpenseTagsOptions],
  (tags) =>
    Object.values(tags).map((item) => ({
      label: item.content,
      value: item.tag_id,
    })),
);

export const selectTagsLoading = (state: RootState) =>
  state.tagsV2.status === 'pending';
export const selectTagsError = (state: RootState) => state.tagsV2.error;

export const tagsV2Reducer = tagsV2Slice.reducer;
